import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, Loader2, AlertCircle, Sparkles, ExternalLink, Copy, Check,
  Settings2, ClipboardList, Trophy,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface InsightItem {
  icon: string;
  title: string;
  detail: string;
  link?: string;
}

interface Brief {
  date: string;
  insightsJson: string | null;
  briefText: string | null;
  model: string | null;
  createdAt: number;
}

interface OpsAction {
  id: number;
  actionDate: string;
  type: string;
  name: string;
  source: 'auto' | 'manual';
  notes?: string | null;
  effectJson?: string | null;
  analyzedAt?: number | null;
  createdAt: number;
}

interface EffectSummary {
  type: string;
  count: number;
  avgOrderLift: number | null;
  avgWaLift: number | null;
  names: Array<{ name: string; date: string; lift: number | null; confidence: string }>;
}

const TYPE_LABELS: Record<string, string> = {
  promotion: '推廣/優惠', broadcast: '廣播', content: '內容/CMS',
  ad: '廣告投放', event: '活動', price: '產品/價格', other: '其他',
};

function parseRules(brief: Brief): InsightItem[] {
  try {
    const arr = JSON.parse(brief.insightsJson || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); } catch {
      const el = document.createElement('textarea');
      el.value = text; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-bold shrink-0 ${copied ? 'bg-green-600 text-white' : 'bg-stone-200 text-stone-700 hover:bg-stone-300 dark:bg-gray-700 dark:text-gray-200'}`}>
      {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? '已複製' : '複製'}
    </button>
  );
}

export default function AdminInsights() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'briefs' | 'actions' | 'effects'>('briefs');
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [actions, setActions] = useState<OpsAction[]>([]);
  const [effects, setEffects] = useState<EffectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // prompt 設置
  const [showSetup, setShowSetup] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [promptSource, setPromptSource] = useState<'custom' | 'default'>('default');
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptSaved, setPromptSaved] = useState(false);

  // 打卡表單
  const [checkType, setCheckType] = useState('promotion');
  const [checkName, setCheckName] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [checkSubmitting, setCheckSubmitting] = useState(false);
  const [checkOk, setCheckOk] = useState(false);

  const loadAll = async () => {
    try {
      const [b, a, e] = await Promise.all([
        apiFetch('/api/admin/insights?days=30'),
        apiFetch('/api/admin/insights/actions?days=30'),
        apiFetch('/api/admin/insights/effects?days=30'),
      ]);
      if (b.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin'); return; }
      setBriefs((await b.json())?.data?.briefs || []);
      setActions((await a.json())?.data?.actions || []);
      setEffects((await e.json())?.data?.summary || []);
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    loadAll();
    apiFetch('/api/admin/insights/prompt')
      .then((r) => r.json())
      .then((d) => { setPrompt(d.data?.prompt || ''); setPromptSource(d.data?.source || 'default'); })
      .catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrompt = async (reset = false) => {
    setPromptSaving(true);
    try {
      const res = await apiFetch('/api/admin/insights/prompt', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: reset ? '' : prompt }),
      });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || '儲存失敗');
      setPromptSource(d.source || (reset ? 'default' : 'custom'));
      if (reset) setPrompt('');
      setPromptSaved(true);
      setTimeout(() => setPromptSaved(false), 2000);
    } catch (err: any) {
      setError(err.message || '儲存失敗');
    } finally {
      setPromptSaving(false);
    }
  };

  const submitCheckIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkName.trim()) return;
    setCheckSubmitting(true);
    try {
      const res = await apiFetch('/api/admin/insights/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: checkType, name: checkName, actionDate: checkDate || undefined }),
      });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || '打卡失敗');
      setCheckOk(true);
      setCheckName('');
      setTimeout(() => setCheckOk(false), 2000);
      apiFetch('/api/admin/insights/actions?days=30')
        .then((r) => r.json())
        .then((d) => setActions(d.data?.actions || []));
    } catch (err: any) {
      setError(err.message || '打卡失敗');
    } finally {
      setCheckSubmitting(false);
    }
  };

  const renderEffectBadge = (action: OpsAction) => {
    if (!action.effectJson) {
      return <span className="text-xs text-gray-400">待分析（動作後 3 日）</span>;
    }
    try {
      const eff = JSON.parse(action.effectJson);
      const lift = eff.lift?.orders;
      const conf = eff.confidence || 'low';
      const confColor = conf === 'high' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
        : conf === 'medium' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
      return (
        <span className="inline-flex items-center gap-1 text-xs">
          <span className={`px-1.5 py-0.5 rounded font-bold ${lift === null || lift === undefined ? 'bg-gray-100 text-gray-500' : lift > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
            {lift === null || lift === undefined ? '—' : `${lift > 0 ? '+' : ''}${lift}%`}
          </span>
          <span className={`px-1.5 py-0.5 rounded ${confColor}`}>{conf === 'high' ? '高信心' : conf === 'medium' ? '中信心' : '低信心'}</span>
        </span>
      );
    } catch {
      return null;
    }
  };

  return (
    <AdminLayout currentPage="insights">
      <div className="mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Brain className="w-6 h-6 text-red-600" />
              每日洞見
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              每晚 01:00 自動生成：指標 → 上週同日對比 → 洞見 → AI 短評＋即用建議；並對營運動作做事後效果歸因。
            </p>
          </div>
          <button
            onClick={() => setShowSetup((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:border-red-400"
          >
            <Settings2 size={15} /> 大腦設置
          </button>
        </div>

        {showSetup && (
          <div className="mt-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h2 className="font-bold text-gray-800 dark:text-white text-sm flex items-center gap-1">
                <Settings2 size={14} /> DeepSeek 系統提示詞
                <span className={`text-xs px-2 py-0.5 rounded-full ${promptSource === 'custom' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                  {promptSource === 'custom' ? '自訂版本' : '內建預設'}
                </span>
              </h2>
              <div className="flex items-center gap-2">
                {promptSaved && <span className="text-xs text-green-600 font-bold">已儲存 ✓ 今晚生效</span>}
                {promptSource === 'custom' && (
                  <button onClick={() => savePrompt(true)} disabled={promptSaving} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    回復預設
                  </button>
                )}
                <button
                  onClick={() => savePrompt(false)}
                  disabled={promptSaving || !prompt.trim()}
                  className="text-xs px-4 py-1.5 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 disabled:opacity-40"
                >
                  {promptSaving ? '儲存中…' : '儲存'}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-2">
              控制 AI 分析員的語氣、心理槓桿與建議格式。規則：AI 只准引用系統提供的真實數字；清空並儲存＝回復內建預設。改動今晚 01:00 生成時生效。
            </p>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={10}
              placeholder="（空白＝使用內建預設提示詞）"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs font-mono outline-none bg-white dark:bg-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-red-500/30"
            />
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-gray-200 dark:border-gray-700">
        {([
          { id: 'briefs', label: '每日簡報', icon: Sparkles },
          { id: 'actions', label: '動作打卡與效果', icon: ClipboardList },
          { id: 'effects', label: '效果排行', icon: Trophy },
        ] as const).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === t.id ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin mr-2" /> 載入中…
        </div>
      ) : (
        <>
          {/* ---------- 每日簡報 ---------- */}
          {tab === 'briefs' && (
            briefs.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                <Brain className="mx-auto mb-3 text-gray-300 dark:text-gray-600" size={44} />
                <p>尚未有簡報——第一份會在今晚 01:00（HKT）生成，並同步發送 WhatsApp。</p>
              </div>
            ) : (
              <div className="space-y-4">
                {briefs.map((b) => {
                  const rules = parseRules(b);
                  return (
                    <div key={b.date} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="font-bold text-gray-900 dark:text-white">📅 {b.date}</h2>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${b.model === 'deepseek-chat' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                          {b.model === 'deepseek-chat' ? '🤖 DeepSeek' : '📋 規則摘要'}
                        </span>
                      </div>
                      {rules.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {rules.map((r, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm">
                              <span className="shrink-0">{r.icon}</span>
                              <div className="min-w-0">
                                <span className="font-bold text-gray-800 dark:text-gray-100">{r.title}</span>
                                <span className="text-gray-500 dark:text-gray-400"> — {r.detail}</span>
                                {r.link && (
                                  <a href={r.link} className="ml-1 inline-flex items-center gap-0.5 text-red-600 hover:underline dark:text-red-400">
                                    去處理 <ExternalLink size={11} />
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {b.briefText && (
                        <div className="bg-stone-50 dark:bg-gray-900/40 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line leading-relaxed">
                          <span className="inline-flex items-center gap-1 font-bold text-stone-500 dark:text-gray-400"><Sparkles size={13} /> 分析員（含即用材料）</span>
                          {'\n'}{b.briefText}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}

          {/* ---------- 動作打卡與效果 ---------- */}
          {tab === 'actions' && (
            <div className="space-y-5">
              <form onSubmit={submitCheckIn} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <h2 className="font-bold text-gray-800 dark:text-white text-sm mb-1">✅ 動作打卡（10 秒）</h2>
                <p className="text-xs text-gray-400 mb-3">
                  系統外的動作（FB/IG 帖、廣告、KOL、實體活動）記一筆——大腦會在 3 日後自動做「前後對比」歸因。系統內動作（建優惠、發廣播等）會自動捕捉，不用打卡。
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={checkType} onChange={(e) => setCheckType(e.target.value)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white">
                    {Object.entries(TYPE_LABELS).filter(([k]) => k !== 'other').map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                    <option value="other">其他</option>
                  </select>
                  <input
                    type="date"
                    value={checkDate}
                    onChange={(e) => setCheckDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white"
                    title="留空＝今日"
                  />
                  <input
                    value={checkName}
                    onChange={(e) => setCheckName(e.target.value)}
                    placeholder="動作名稱，例如：FB 帖——辣椒炒蟹食譜"
                    className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 dark:text-white"
                  />
                  <button type="submit" disabled={checkSubmitting || !checkName.trim()} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-40">
                    {checkSubmitting ? '…' : checkOk ? '✓ 已記錄' : '打卡'}
                  </button>
                </div>
              </form>

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 text-sm font-bold text-gray-700 dark:text-gray-200">
                  最近 30 日動作（{actions.length}）
                </div>
                {actions.length === 0 ? (
                  <p className="p-8 text-center text-sm text-gray-400">還沒有任何動作記錄——打卡第一筆，或今晚起系統會自動捕捉後台動作。</p>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {actions.map((a) => (
                      <div key={a.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                            <span className="text-xs px-1.5 py-0.5 rounded bg-stone-100 dark:bg-gray-700 text-stone-600 dark:text-gray-300 mr-1.5">{TYPE_LABELS[a.type] || a.type}</span>
                            {a.name}
                            {a.source === 'auto' && <span className="ml-1.5 text-[10px] text-gray-400">自動捕捉</span>}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">📅 {a.actionDate}</div>
                        </div>
                        {renderEffectBadge(a)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ---------- 效果排行 ---------- */}
          {tab === 'effects' && (
            effects.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                <Trophy className="mx-auto mb-3 text-gray-300 dark:text-gray-600" size={44} />
                <p>累積約一週動作＋分析後，這裡會顯示「哪類動作平均提升最高」——直接指引下個月多做什麼。</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">動作類型</th>
                        <th className="px-4 py-3 text-center font-medium">次數</th>
                        <th className="px-4 py-3 text-center font-medium">平均訂單提升</th>
                        <th className="px-4 py-3 text-center font-medium">平均 WA 入站提升</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {effects.map((e) => (
                        <tr key={e.type}>
                          <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{TYPE_LABELS[e.type] || e.type}</td>
                          <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{e.count}</td>
                          <td className={`px-4 py-3 text-center font-bold ${e.avgOrderLift === null ? 'text-gray-400' : e.avgOrderLift > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {e.avgOrderLift === null ? '—' : `${e.avgOrderLift > 0 ? '+' : ''}${e.avgOrderLift}%`}
                          </td>
                          <td className={`px-4 py-3 text-center ${e.avgWaLift === null ? 'text-gray-400' : e.avgWaLift > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {e.avgWaLift === null ? '—' : `${e.avgWaLift > 0 ? '+' : ''}${e.avgWaLift}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-400 text-center">信心標記口徑：±3 日內無其他動作且基線樣本 ≥3＝高；有重疊＝中；樣本不足＝低。相關性≠因果，僅供方向判斷。</p>
              </div>
            )
          )}
        </>
      )}
    </AdminLayout>
  );
}
